import { canonicalizeTaskNumber } from '@internal/spassu-code-contract';

import type { TaskItem } from '../../api/types';
import type {
  DiscoveredTaskSummary,
  RunningTaskOptions,
} from './streamUiReducer';

/** Helpers puros de tasks do `streamUiReducer` (estado de execução). */
export function currentIsoTimestamp(): string {
  return new Date().toISOString();
}

function normalizeDependsOn(dependsOn?: string[]): string[] {
  return Array.from(
    new Set((dependsOn ?? []).map(dep => canonicalizeTaskNumber(dep))),
  );
}

function createDiscoveredTask(
  task: DiscoveredTaskSummary,
  now: string,
): TaskItem {
  const canonical = canonicalizeTaskNumber(task.taskNumber);
  return {
    id: `discovered-${canonical}`,
    activityId: '',
    taskNumber: canonical,
    title: task.title,
    dependsOn: normalizeDependsOn(task.dependsOn),
    status: 'pending',
    executionLog: null,
    errorMessage: null,
    integrated: null,
    integratedAt: null,
    parallelSlot: null,
    totalSlots: null,
    worktreePath: null,
    lastEventAt: null,
    startedAt: null,
    completedAt: null,
    createdAt: now,
  };
}

export function mergeDiscoveredTasks(
  currentTasks: TaskItem[],
  discovered: DiscoveredTaskSummary[],
): TaskItem[] {
  const now = currentIsoTimestamp();
  const discoveredByNumber = new Map(
    discovered.map(task => [canonicalizeTaskNumber(task.taskNumber), task]),
  );
  const existingNumbers = new Set(
    currentTasks.map(task => canonicalizeTaskNumber(task.taskNumber)),
  );
  const updatedTasks = currentTasks.map(task => {
    const canonical = canonicalizeTaskNumber(task.taskNumber);
    const update = discoveredByNumber.get(canonical);
    if (!update) return task;
    return {
      ...task,
      taskNumber: canonical,
      title: update.title || task.title,
      dependsOn: normalizeDependsOn(update.dependsOn),
    };
  });
  const newTasks = discovered
    .filter(
      task => !existingNumbers.has(canonicalizeTaskNumber(task.taskNumber)),
    )
    .map(task => createDiscoveredTask(task, now));
  return [...updatedTasks, ...newTasks];
}

export function mergeLoadedTasks(
  loadedTasks: TaskItem[],
  currentTasks: TaskItem[],
): TaskItem[] {
  const currentByNumber = new Map(
    currentTasks.map(task => [canonicalizeTaskNumber(task.taskNumber), task]),
  );
  return loadedTasks.map(task => {
    const canonical = canonicalizeTaskNumber(task.taskNumber);
    const current = currentByNumber.get(canonical);
    return {
      ...task,
      taskNumber: canonical,
      dependsOn: normalizeDependsOn(task.dependsOn),
      integrated: task.integrated ?? current?.integrated,
      integratedAt: task.integratedAt ?? current?.integratedAt,
      lastEventAt:
        task.lastEventAt ??
        current?.lastEventAt ??
        task.completedAt ??
        task.startedAt ??
        null,
    };
  });
}

function toRunningTask(
  task: TaskItem,
  canonical: string,
  now: string,
  title?: string,
  options: RunningTaskOptions = {},
): TaskItem {
  return {
    ...task,
    taskNumber: canonical,
    title: title || task.title,
    status: 'running',
    errorMessage: null,
    integrated: false,
    integratedAt: null,
    startedAt: task.startedAt ?? now,
    lastEventAt: now,
    parallelSlot: options.slot ?? task.parallelSlot ?? null,
    totalSlots: options.totalSlots ?? task.totalSlots ?? null,
  };
}

function createRunningTask(
  canonical: string,
  now: string,
  title?: string,
  options: RunningTaskOptions = {},
): TaskItem {
  return {
    id: `worker-${canonical}`,
    activityId: '',
    taskNumber: canonical,
    title: title ?? `Task ${canonical}`,
    dependsOn: [],
    status: 'running',
    executionLog: null,
    errorMessage: null,
    integrated: false,
    integratedAt: null,
    parallelSlot: options.slot ?? null,
    totalSlots: options.totalSlots ?? null,
    worktreePath: null,
    lastEventAt: now,
    startedAt: now,
    completedAt: null,
    createdAt: now,
  };
}

export function upsertRunningTask(
  tasks: TaskItem[],
  taskNumber: string,
  title?: string,
  options: RunningTaskOptions = {},
): TaskItem[] {
  const now = currentIsoTimestamp();
  const canonical = canonicalizeTaskNumber(taskNumber);
  const exists = tasks.some(
    task => canonicalizeTaskNumber(task.taskNumber) === canonical,
  );
  if (!exists) {
    return [...tasks, createRunningTask(canonical, now, title, options)];
  }
  return tasks.map(task =>
    canonicalizeTaskNumber(task.taskNumber) === canonical
      ? toRunningTask(task, canonical, now, title, options)
      : task,
  );
}

export function updateTaskLastEvent(
  tasks: TaskItem[],
  taskNumber: string | null,
): TaskItem[] {
  if (!taskNumber) return tasks;
  const canonical = canonicalizeTaskNumber(taskNumber);
  const now = currentIsoTimestamp();
  return tasks.map(task =>
    canonicalizeTaskNumber(task.taskNumber) === canonical
      ? { ...task, lastEventAt: now }
      : task,
  );
}

export function markTaskIntegrated(
  tasks: TaskItem[],
  input: {
    taskNumber: string;
    integratedAt: string;
    slot?: number;
    totalSlots?: number;
    worktreePath?: string;
  },
): TaskItem[] {
  const canonical = canonicalizeTaskNumber(input.taskNumber);
  const applyIntegrated = (task: TaskItem): TaskItem => ({
    ...task,
    taskNumber: canonical,
    status: 'completed',
    integrated: true,
    integratedAt: input.integratedAt,
    lastEventAt: input.integratedAt,
    parallelSlot: input.slot ?? task.parallelSlot ?? null,
    totalSlots: input.totalSlots ?? task.totalSlots ?? null,
    worktreePath: input.worktreePath ?? task.worktreePath ?? null,
  });
  if (
    tasks.some(task => canonicalizeTaskNumber(task.taskNumber) === canonical)
  ) {
    return tasks.map(task =>
      canonicalizeTaskNumber(task.taskNumber) === canonical
        ? applyIntegrated(task)
        : task,
    );
  }
  return [
    ...tasks,
    applyIntegrated({
      id: `integrated-${canonical}`,
      activityId: '',
      taskNumber: canonical,
      title: `Task ${canonical}`,
      dependsOn: [],
      status: 'completed',
      executionLog: null,
      errorMessage: null,
      startedAt: null,
      completedAt: input.integratedAt,
      createdAt: input.integratedAt,
    }),
  ];
}
